import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RiBuilding2Line, RiLinkM, RiArrowLeftSLine, RiArrowRightSLine, RiSearchLine, RiCloseLine } from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { Badge } from '@components/ui/Badge';
import { Skeleton } from '@components/ui/Skeleton';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@components/ui/Table';
import Button from '@components/ui/Button';
import Swal from 'sweetalert2';
import { apiClient } from '@/lib/api';

const STRIPE_STATUS_CONFIG = {
  active: { label: 'Active', className: 'bg-green-100 text-green-800 border-green-200' },
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
};

const BrokerManagementPage = () => {
  const queryClient = useQueryClient();
  const [creatingAccountFor, setCreatingAccountFor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data: brokers, isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => apiClient.getOrganizations(),
    staleTime: 30000,
  });

  const handleCreateAccount = async (broker) => {
    const { organization_id: orgId, name: orgName, billing_email: billingEmail } = broker;
    setCreatingAccountFor(orgId);
    const { payment_platforms, ...brokerPayload } = broker;
    await apiClient.updateOrganization(orgId, brokerPayload);

    try {
      
      const result = await apiClient.startStripeOnboarding(orgId);

      if (result.account_link_url) {

        if (billingEmail) {
          await apiClient.sendMessage({
            From: "tools@trustjet.ai",
            To: billingEmail,
            Subject: `Stripe Connect Onboarding - ${orgName}`,
            HtmlBody: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #1a1a1a;">Hello ${orgName},</h2>
  <p style="color: #4a4a4a; font-size: 16px; line-height: 1.6;">Please complete your Stripe Connect onboarding by clicking the button below:</p>
  <div style="text-align: center; margin: 32px 0;">
    <a href="${result.account_link_url}" target="_blank" rel="noopener noreferrer" style="background-color: #4F46E5; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 8px; font-size: 16px; font-weight: 600; display: inline-block;">Complete Onboarding</a>
  </div>
  <p style="color: #6b7280; font-size: 14px; line-height: 1.5;">If the button above doesn't work, copy and paste this link into your browser:</p>
  <p style="color: #4F46E5; font-size: 14px; word-break: break-all;">${result.account_link_url}</p>
  <p style="color: #4a4a4a; font-size: 16px; margin-top: 24px;">Thank you</p>
</div>`,
          });

          await Swal.fire({
            icon: 'success',
            title: 'Onboarding Link Sent',
            html: `Stripe onboarding link has been sent to <strong>${billingEmail}</strong>.`,
            confirmButtonColor: '#4F46E5',
          });
          
        }

        queryClient.invalidateQueries({ queryKey: ['organizations'] });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create Stripe Connected Account. Please try again.',
        confirmButtonColor: '#4F46E5',
      });
    } finally {
      setCreatingAccountFor(null);
    }
  };

  const filteredBrokers = useMemo(() => {
    if (!brokers) return [];
    if (!searchQuery.trim()) return brokers;
    const query = searchQuery.toLowerCase();
    return brokers.filter((broker) =>
      broker.name?.toLowerCase().includes(query) ||
      broker.billing_email?.toLowerCase().includes(query) ||
      broker.stripe_account_id?.toLowerCase().includes(query)
    );
  }, [brokers, searchQuery]);

  const totalItems = filteredBrokers.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedBrokers = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredBrokers.slice(start, start + pageSize);
  }, [filteredBrokers, currentPage, pageSize]);

  const renderStripeStatus = (broker) => {
    if (!broker.stripe_account_id) {
      return <Badge className="bg-gray-100 text-gray-600 border-gray-200">Not Connected</Badge>;
    }

    const config = STRIPE_STATUS_CONFIG[broker.stripe_status] || STRIPE_STATUS_CONFIG.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="pb-20">
        <div className="max-w-full">
          <Card className="overflow-hidden !border-gray-100 !bg-transparent !shadow-none !border-0">
            <CardContent className="!p-1">
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-5 w-56" />
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="h-8 w-40" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!brokers || brokers.length === 0) {
    return (
      <div className="pb-20">
        <div className="max-w-full">
          <Card className="max-w-md mx-auto">
            <CardContent className="p-12 text-center space-y-4">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                <RiBuilding2Line className="w-10 h-10 text-gray-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No organizations found</h3>
                <p className="text-sm text-gray-500">
                  There are no broker organizations to manage at this time.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      <div className="max-w-full">
        <Card className="overflow-hidden border border-gray-100 rounded-2xl">
          <CardContent className="p-0">
            {/* Search */}
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="relative max-w-sm">
                <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, or Stripe ID..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(0); }}
                  className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
                {searchQuery && (
                  <button
                    onClick={() => { setSearchQuery(''); setCurrentPage(0); }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <RiCloseLine className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/50">
                  <TableHead className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Organization
                  </TableHead>
                  <TableHead className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Email
                  </TableHead>
                  <TableHead className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Stripe Status
                  </TableHead>
                  <TableHead className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Stripe Account
                  </TableHead>
                  <TableHead className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedBrokers.map((broker) => (
                  <TableRow key={broker.organization_id}>
                    <TableCell className="px-4 py-3 font-medium text-gray-900">
                      {broker.name}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-gray-600">
                      {broker.billing_email || '—'}
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      {renderStripeStatus(broker)}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-gray-500 text-xs font-mono">
                      {broker.stripe_account_id || '—'}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      {!broker.stripe_account_id && (
                        <Button
                          size="small"
                          variant="primary"
                          mode="filled"
                          disabled={creatingAccountFor === broker.organization_id}
                          onClick={() => handleCreateAccount(broker)}
                        >
                          {creatingAccountFor === broker.organization_id ? (
                            <span className="flex items-center gap-2">
                              <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></span>
                              Creating...
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              <RiLinkM className="w-3.5 h-3.5" />
                              Stripe Connect Onboarding
                            </span>
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {totalItems > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(0); }}
                    className="border border-gray-200 rounded-md px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {[5, 10, 25, 50].map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  <span className="ml-2">
                    {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, totalItems)} of {totalItems}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <RiArrowLeftSLine className="w-5 h-5 text-gray-600" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`min-w-[32px] h-8 px-2 rounded-md text-sm font-medium transition-colors ${
                        i === currentPage
                          ? 'bg-primary-500 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <RiArrowRightSLine className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BrokerManagementPage;
